import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { combine } from '@atlaskit/pragmatic-drag-and-drop/combine';
import {
  draggable,
  dropTargetForElements,
} from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { disableNativeDragPreview } from '@atlaskit/pragmatic-drag-and-drop/element/disable-native-drag-preview';
import { DRAGGABLE, DRAGGABLE_TARGET, useDndContext } from './DragDropProvider';
import { useDroppable } from './Droppable';
import { getClosestEdge } from './edge';

// `as` sets the rendered element (default 'div'); use as="tr" inside a <Droppable as="tbody">
// `solidDragPreview` replaces the browser's semi-transparent drag image with a fully opaque copy that follows the pointer
// `isDragDisabled` stops the item from being dragged (it can still be dropped around)
// Function children receive { isDragging, isDragDisabled, closestEdge }
export function Draggable({ draggableId, index, showPlaceholder = true, solidDragPreview = false, isDragDisabled = false, as: Element = 'div', style, children, ...rest }) {
  const ref = useRef(null);
  const previewRef = useRef(null);
  const { instanceId, register } = useDndContext();
  const { droppableId, direction } = useDroppable();
  const [isDragging, setIsDragging] = useState(false);
  const [closestEdge, setClosestEdge] = useState(null);
  const [placeholderSize, setPlaceholderSize] = useState(null);

  useEffect(() => register(droppableId, draggableId), [register, droppableId, draggableId]);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const canDrop = ({ source }) =>
      source.data.type === DRAGGABLE && source.data.instanceId === instanceId;

    // Opaque preview (only used when solidDragPreview is set)
    let pointerOffset = { x: 0, y: 0 };
    const movePreview = ({ clientX, clientY }) => {
      if (previewRef.current) {
        previewRef.current.style.transform = `translate(${clientX - pointerOffset.x}px, ${clientY - pointerOffset.y}px)`;
      }
    };
    const removePreview = () => {
      if (previewRef.current) {
        previewRef.current.remove();
        previewRef.current = null;
      }
    };

    const cleanup = combine(
      draggable({
        element,
        canDrag: () => !isDragDisabled,
        onGenerateDragPreview: ({ nativeSetDragImage, location }) => {
          if (!solidDragPreview) return;
          disableNativeDragPreview({ nativeSetDragImage });

          // Copy the item while it is still visible (it is hidden once dragging starts)
          const rect = element.getBoundingClientRect();
          const { clientX, clientY } = location.current.input;
          pointerOffset = { x: clientX - rect.left, y: clientY - rect.top };
          const preview = element.cloneNode(true);
          previewRef.current = preview;
          preview.removeAttribute('data-draggable-id');
          Object.assign(preview.style, {
            position: 'fixed',
            top: '0px',
            left: '0px',
            width: `${rect.width}px`,
            height: `${rect.height}px`,
            boxSizing: 'border-box',
            margin: '0px',
            pointerEvents: 'none',
            zIndex: '9999',
            opacity: '1',
            display: '',
          });
          document.body.appendChild(preview);
          movePreview({ clientX, clientY });
        },
        onDrag: ({ location }) => movePreview(location.current.input),
        getInitialData: ({ element }) => {
          const rect = element.getBoundingClientRect();
          return {
            type: DRAGGABLE,
            draggableId,
            droppableId,
            index,
            instanceId,
            size: { width: rect.width, height: rect.height }, // for the placeholder
          };
        },
        onDragStart: () => setIsDragging(true),
        onDrop: () => {
          removePreview();
          setIsDragging(false);
        },
      }),
      dropTargetForElements({
        element,
        canDrop,
        getData: ({ input, element }) => ({
          type: DRAGGABLE_TARGET,
          draggableId,
          droppableId,
          index,
          edge: getClosestEdge(input, element, direction),
        }),
        getIsSticky: () => true,
        onDrag: ({ self, source }) => {
          if (source.data.draggableId === draggableId) {
            setClosestEdge(null);
            return;
          }
          setClosestEdge(self.data.edge);
          setPlaceholderSize(source.data.size);
        },
        onDragLeave: () => setClosestEdge(null),
        onDrop: () => setClosestEdge(null),
      }),
    );

    return () => {
      removePreview();
      cleanup();
    };
  }, [draggableId, droppableId, index, direction, instanceId, solidDragPreview, isDragDisabled]);

  // The preview is copied before isDragging re-renders the item, so copy the new content
  // (e.g. dragging styles from function children) into it once that render is done
  useLayoutEffect(() => {
    if (previewRef.current && ref.current) {
      previewRef.current.innerHTML = ref.current.innerHTML;
    }
  }, [isDragging]);

  const showBefore = showPlaceholder && (closestEdge === 'top' || closestEdge === 'left');
  const showAfter = showPlaceholder && (closestEdge === 'bottom' || closestEdge === 'right');

  return (
    <>
      {showBefore && <Placeholder size={placeholderSize} isRow={Element === 'tr'} />}
      <Element
        ref={ref}
        data-draggable-id={draggableId}
        data-dragging={isDragging || undefined}
        style={{ ...style, ...(isDragging ? { display: 'none' } : null) }}
        {...rest}
      >
        {typeof children === 'function' ? children({ isDragging, isDragDisabled, closestEdge }) : children}
      </Element>
      {showAfter && <Placeholder size={placeholderSize} isRow={Element === 'tr'} />}
    </>
  );
}

// A real element in the layout (sibling of the items), so items shift in lists and grids
function Placeholder({ size, isRow }) {
  // Inside a <tbody> only <tr> is valid, so render an empty row of the same height
  if (isRow) {
    return (
      <tr data-dnd-placeholder style={{ height: size?.height, pointerEvents: 'none' }}>
        <td colSpan={100} style={{ padding: 0, border: 0 }} />
      </tr>
    );
  }
  return (
    <div
      data-dnd-placeholder
      style={{
        width: size?.width,
        height: size?.height,
        flexShrink: 0,
        boxSizing: 'border-box',
        borderRadius: 6,
        pointerEvents: 'none',
      }}
    />
  );
}